<template>
  <card-dashboard class="calendar-upcoming-events">
    <div class="flex items-center justify-between mb-7">
      <div class="md-title font-bold text-xl">
        {{ $t("dashboard.upcoming_events") }}
      </div>
      <router-link :to="calendarRoute">
        <button class="w-[32px] h-[32px]">
          <md-icon>chevron_right</md-icon>
        </button>
      </router-link>
    </div>

    <div
      class="md-layout mb-5"
      v-for="(event, index) in events"
      :key="'upcoming events' + index"
    >
      <div class="md-layout-item md-small-size-100 md-size-50 px-0 md:pl-0">
        <div class="flex items-center">
          <div>
            <md-avatar class="mr-2 text-xl" :style="styleColor(event.label)">
              {{ event.label }}
            </md-avatar>
          </div>
          <div class="event-title">
            <p>{{ event.title }}</p>
            <p class="sub-title text-sm">{{ event.subTitle }}</p>
          </div>
        </div>
      </div>
      <div class="md-layout-item md-small-size-100 md-size-50 text-right px-0">
        <div class="mb-2">
          <span
            class="font-medium text-sm rounded p-1"
            :style="styleColor(event.label)"
            >{{ event.time }}</span
          >
        </div>
        <router-link
          v-if="showTakeAttendance && event.attendanceRoute"
          class="text-blue-700 underline hover:text-blue-700"
          :to="event.attendanceRoute"
        >
          {{ $t("dashboard.take_attendance") }}
          <i class="fas fa-chevron-right text-xs"></i>
        </router-link>
        <a
          v-if="event.zoomMeeting"
          :href="event.zoomMeeting.link_zoom"
          target="_blank"
          >{{ $t("dashboard.join_meeting") }} &gt;
        </a>
      </div>
    </div>
  </card-dashboard>
</template>
<script>
import { DAYS_IN_WEEK } from "./upcoming-events-config";
import CardDashboard from "./CardDashboard";
import { getTimeString, addDayOfDate } from "@/helpers/datetime-helper";
import { mapGetters } from "vuex";
import { schedule_names } from "@/routes/routes";
import moment from "moment-timezone";
import { getId } from "@/helpers/utils";
import CalendarConst from "@/constants/Calendar";
import { TEACHER_ASSIGNMENTS } from "@/constants";
import { formatDateClient } from "../../../helpers/datetime-helper";
const clientLocale = localStorage.getItem("language");
const formatDateTimeClient =
  clientLocale === "en" ? "MM-DD-YYYY hh:mm A" : "DD-MM-YYYY hh:mm A";

export default {
  name: "upcoming-events",
  components: { CardDashboard },
  data() {
    return {
      events: [],
      calendarRoute: {
        name: schedule_names.listPage
      }
    };
  },
  props: ["path", "showTakeAttendance"],
  created() {
    this.fetchUpcomingEvents();
  },
  computed: {
    ...mapGetters("user", {
      userTz: "timezone"
    })
  },
  methods: {
    fetchUpcomingEvents() {
      if (!this.path) return;
      let startDateTime = new Date();
      startDateTime.setHours(startDateTime.getHours() - 2);
      const start = getTimeString(
        startDateTime.toISOString(),
        "Etc/UTC",
        "YYYY-MM-DD hh:mm:ss"
      );
      const startDate = getTimeString(
        new Date().toISOString(),
        "Etc/UTC",
        "YYYY-MM-DD"
      );
      const end = addDayOfDate(
        getTimeString(
          new Date().toISOString(),
          "Etc/UTC",
          "YYYY-MM-DD hh:mm:ss"
        ),
        7,
        "YYYY-MM-DD hh:mm:ss"
      );
      const endDate = addDayOfDate(
        getTimeString(
          new Date().toISOString(),
          "Etc/UTC",
          "YYYY-MM-DD hh:mm:ss"
        ),
        7,
        "YYYY-MM-DD"
      );

      this.$axios
        .get(this.path, {
          start: start,
          end: end,
          start_date: startDate,
          end_date: endDate
        })
        .then(({ data }) => {
          const formattedEvents = this.formattedEvents(
            data?.sort(this.sortEventsByStartTime)
          );
          this.events = formattedEvents;
        });
    },

    sortEventsByStartTime(firstEvent, secondEvent) {
      const firstStartTime = moment(firstEvent.start);
      const secondStartTime = moment(secondEvent.start);

      if (firstStartTime.isBefore(secondStartTime)) return -1;

      return 1;
    },

    formattedEvents(events) {
      if (!events || !events.length) return [];

      return events.map(event => {
        const userTz = this.userTz ?? moment.tz.guess();
        let time = getTimeString(event.start, userTz, formatDateTimeClient);
        if (/^\d{4}-\d{2}-\d{2}$/.test(event.start)) {
          time = getTimeString(event.start, userTz, formatDateClient);
        }
        const label = getTimeString(event.start, userTz, "dd");
        const baseInfo = this.getEventInfo(event);
        const zoomMeeting = event.zoom_meeting;
        return {
          time,
          label,
          ...baseInfo,
          zoomMeeting
        };
      });
    },

    getEventInfo(event) {
      if (event.type === CalendarConst.EVENT_TYPES.schoolEvent) {
        return this.getSchoolEventInfo(event);
      }
      if (event.type === CalendarConst.EVENT_TYPES.meetingEvent) {
        return {
          title: event.name,
          subTitle: this.$t("schedule.virtual_meeting")
        };
      }

      return this.getClassScheduleInfo(event);
    },

    getSchoolEventInfo(event) {
      return {
        title: event.name,
        subTitle: event.category
      };
    },

    getClassScheduleInfo(event) {
      const classObj = event?.class;
      const teachers = classObj?.teachers;

      return {
        title: classObj?.name,
        subTitle: teachers?.find(
          teacher => teacher.assignment === TEACHER_ASSIGNMENTS.PRIMARY
        )?.users?.username,
        attendanceRoute: {
          name: schedule_names.attendance,
          params: {
            id: getId(event)
          }
        }
      };
    },

    styleColor(label) {
      let dataTest = DAYS_IN_WEEK.find(el => el.label === label);
      return {
        color: dataTest?.color,
        backgroundColor: dataTest?.backgroundColor
      };
    }
  },
  watch: {
    path() {
      this.fetchUpcomingEvents();
    }
  }
};
</script>
<style lang="scss">
.calendar-upcoming-events {
  max-height: 800px;
  overflow-y: auto;
  button {
    border-radius: 10px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
  }
  .event-title .sub-title {
    color: #9f9f9f;
  }
}
</style>
